import React, { useMemo, useRef } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import CardActions from './CardActions';
import { getGuideColors, GuideHeader, GuideSection, GuideSummary, GuideTips, guideStyles } from './GuideCardLayout';

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1);

function RecipeCard({ spec, colors: themeColors, conversationId, messageId }) {
  const captureTargetRef = useRef(null);
  const colors = useMemo(() => getGuideColors(themeColors, 'recipe'), [themeColors]);
  const metadata = [spec.cuisine, ...spec.tags].filter(Boolean).join(' · ');

  return (
    <View
      accessible={false}
      accessibilityRole="summary"
      accessibilityLabel={`Recipe: ${spec.title}. ${spec.ingredients.length} ingredients, ${spec.steps.length} steps.`}
      style={[guideStyles.container, { backgroundColor: colors.background, borderColor: colors.border }]}
    >
      <View ref={captureTargetRef} collapsable={false} dataSet={{ guideKind: 'recipe', cardCapture: '' }} style={[guideStyles.captureTarget, { backgroundColor: colors.background }]}>
        <GuideHeader kind="Recipe" icon="utensils" title={spec.title} description={spec.description} metadata={metadata} colors={colors} />
        <GuideSummary
          items={[
            { label: 'Total time', value: spec.totalTime },
            { label: 'Serves', value: spec.servings },
            { label: 'Level', value: spec.difficulty ? capitalize(spec.difficulty) : '' },
          ]}
          details={[
            { label: 'Prep', value: spec.prepTime },
            { label: 'Cook', value: spec.cookTime },
          ]}
          colors={colors}
        />

        <GuideSection title="Ingredients" detail={`${spec.ingredients.length} ${spec.ingredients.length === 1 ? 'item' : 'items'}`} colors={colors} />
        {spec.ingredientGroups.map((group, groupIndex) => (
          <View key={`group-${groupIndex}`} style={guideStyles.group}>
            {!!group.section && <Text selectable style={[guideStyles.groupTitle, { color: colors.secondary }]}>{group.section}</Text>}
            {group.items.map((ingredient, index) => (
              <View key={index} style={[styles.ingredient, index > 0 && { borderTopWidth: 1, borderColor: colors.border }]}>
                <View style={[styles.ingredientDot, { backgroundColor: colors.accent }]} />
                <Text selectable style={[guideStyles.bodyText, { color: colors.primary }]}>{ingredient}</Text>
              </View>
            ))}
          </View>
        ))}

        <GuideSection title="Instructions" detail={`${spec.steps.length} ${spec.steps.length === 1 ? 'step' : 'steps'}`} colors={colors} />
        {spec.steps.map((step, index) => (
          <View key={index} style={styles.step}>
            <View style={[guideStyles.number, { backgroundColor: colors.band }]}>
              <Text selectable style={[guideStyles.numberText, { color: colors.accent }]}>{index + 1}</Text>
            </View>
            <Text selectable style={[guideStyles.bodyText, { color: colors.primary }]}>{step}</Text>
          </View>
        ))}
        <GuideTips title="Chef's Tips" tips={spec.tips} colors={colors} />
      </View>

      <CardActions
        conversationId={conversationId}
        messageId={messageId}
        captureTargetRef={captureTargetRef}
        exportTitle={spec.title}
        accentColor={colors.accent}
        borderColor={colors.border}
        bandColor={colors.band}
        textColor={colors.secondary}
      />
    </View>
  );
}

RecipeCard.propTypes = {
  spec: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
};

const styles = StyleSheet.create({
  ingredient: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10 },
  ingredientDot: { width: 4, height: 4, borderRadius: 2, marginTop: 10, marginRight: 12 },
  step: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
});

export default React.memo(RecipeCard);
